import { CA_PROVINCES } from "./ca-provinces";
import { NZ_REGIONS } from "./nz-regions";
import { US_STATES } from "./us-states";

export type BrokerLicenseKind =
  | "real_estate_license"
  | "business_broker_registration";

export type BrokerOperatingCountry = "US" | "CA" | "NZ";

export type BrokerCurrency = "USD" | "CAD" | "NZD";

export type BrokerRegionOption = {
  code: string;
  name: string;
};

export const BROKER_OPERATING_COUNTRIES: Array<{
  value: BrokerOperatingCountry;
  label: string;
}> = [
  { value: "US", label: "United States" },
  { value: "CA", label: "Canada" },
  { value: "NZ", label: "New Zealand" },
];

export type BrokerAttestationKind =
  | "US_ACTIVE_LICENSE"
  | "CA_PROVINCIAL_REGISTRATION"
  | "NZ_REAA_2008";

export const BROKER_ATTESTATION_VERSION = 1;

export const BROKER_ATTESTATION_STATEMENTS: Record<
  BrokerAttestationKind,
  string
> = {
  US_ACTIVE_LICENSE:
    "I confirm this licence number is mine and is currently active.",
  CA_PROVINCIAL_REGISTRATION:
    "I confirm this licence or registration number is mine and is currently active with my provincial regulator.",
  NZ_REAA_2008:
    "I hold a current licence under the Real Estate Agents Act 2008.",
};

export type BrokerStateRequirements = {
  countryCode: BrokerOperatingCountry | "";
  stateCode: string;
  requiresLicense: boolean;
  requiresBrokerage: boolean;
  licenseKind: BrokerLicenseKind | null;
  note: string | null;
  defaultCurrency: BrokerCurrency;
  attestationKind: BrokerAttestationKind | null;
  /** When true, saving a licence number requires a fresh confirmation. */
  attestationRequiredWithLicense: boolean;
  showsLicenseNumber: boolean;
  licenseNumberOptional: boolean;
};

export type BrokerPrerequisiteField =
  | "photo"
  | "name"
  | "phone"
  | "email"
  | "country"
  | "state"
  | "licenseNumber"
  | "brokerage";

export type BrokerPrerequisiteSnapshot = {
  imageUrl?: string | null;
  name?: string | null;
  phone?: string | null;
  email?: string | null;
  countryCode?: string | null;
  stateCode?: string | null;
  licenseNumber?: string | null;
  brokerageName?: string | null;
};

/** License-hang firm for public display. Independent of the Venturu OS org. */
export function displayBrokerageName(profile: {
  brokerageName?: string | null;
  organization?: { name?: string | null } | null;
} | null | undefined): string | null {
  const fromField = profile?.brokerageName?.trim();
  if (fromField) return fromField;
  const fromOrg = profile?.organization?.name?.trim();
  return fromOrg || null;
}

const REAL_ESTATE_LICENSE_STATES = new Set([
  "AK",
  "AZ",
  "CA",
  "CO",
  "FL",
  "GA",
  "ID",
  "MN",
  "NE",
  "NV",
  "OR",
  "SD",
  "UT",
  "WA",
  "WI",
  "WY",
]);

const SPECIAL_REQUIREMENTS: Record<string, Partial<BrokerStateRequirements>> = {
  IL: {
    requiresLicense: true,
    requiresBrokerage: false,
    licenseKind: "business_broker_registration",
    note: "Illinois requires business broker registration with the Secretary of State Securities Division to collect commissions.",
  },
  NV: {
    note: "Nevada requires a real estate license and a specialized Business Broker Permit for business sales.",
  },
};

const CURRENCY_BY_COUNTRY: Record<BrokerOperatingCountry, BrokerCurrency> = {
  US: "USD",
  CA: "CAD",
  NZ: "NZD",
};

function filled(value: string | null | undefined): boolean {
  return typeof value === "string" && value.trim().length > 0;
}

export function normalizeBrokerCountryCode(
  value: string | null | undefined,
): BrokerOperatingCountry | null {
  const normalized = value?.trim().toUpperCase();
  if (normalized === "US" || normalized === "CA" || normalized === "NZ") {
    return normalized;
  }
  return null;
}

export function currencyForBrokerCountry(
  countryCode: string | null | undefined,
): BrokerCurrency {
  const country = normalizeBrokerCountryCode(countryCode);
  return country ? CURRENCY_BY_COUNTRY[country] : "USD";
}

export function getBrokerRegions(
  countryCode: string | null | undefined,
): BrokerRegionOption[] {
  const country = normalizeBrokerCountryCode(countryCode);
  if (country === "CA") return [...CA_PROVINCES];
  if (country === "NZ") return [...NZ_REGIONS];
  if (country === "US") return US_STATES.map(({ code, name }) => ({ code, name }));
  return [];
}

export function brokerRegionLabel(
  countryCode: string | null | undefined,
): string {
  const country = normalizeBrokerCountryCode(countryCode);
  if (country === "CA") return "Province or territory";
  if (country === "NZ") return "Region";
  return "State";
}

export function brokerRegionPlaceholder(
  countryCode: string | null | undefined,
): string {
  const country = normalizeBrokerCountryCode(countryCode);
  if (country === "CA") return "Choose a province";
  if (country === "NZ") return "Choose a region";
  return "Choose a state";
}

export function brokerCountryLabel(
  countryCode: string | null | undefined,
): string {
  const country = normalizeBrokerCountryCode(countryCode);
  return (
    BROKER_OPERATING_COUNTRIES.find((option) => option.value === country)
      ?.label ??
    countryCode ??
    ""
  );
}

export function isValidBrokerRegion(
  countryCode: string | null | undefined,
  stateCode: string | null | undefined,
): boolean {
  const country = normalizeBrokerCountryCode(countryCode);
  const region = stateCode?.trim().toUpperCase() ?? "";
  if (!country || !region) return false;
  return getBrokerRegions(country).some((option) => option.code === region);
}

export function getBrokerStateRequirements(
  stateCode: string | null | undefined,
  countryCode?: string | null,
): BrokerStateRequirements {
  const country = normalizeBrokerCountryCode(countryCode) ?? "";
  const normalized = stateCode?.trim().toUpperCase() ?? "";
  const defaultCurrency = currencyForBrokerCountry(country || "US");

  if (country === "CA") {
    return {
      countryCode: "CA",
      stateCode: normalized,
      requiresLicense: false,
      requiresBrokerage: false,
      licenseKind: null,
      note: "Canadian provincial licensing rules vary. If you hold a provincial licence or registration, add the number so it can appear on your ads.",
      defaultCurrency: "CAD",
      attestationKind: "CA_PROVINCIAL_REGISTRATION",
      attestationRequiredWithLicense: true,
      showsLicenseNumber: true,
      licenseNumberOptional: true,
    };
  }

  if (country === "NZ") {
    return {
      countryCode: "NZ",
      stateCode: normalized,
      requiresLicense: false,
      requiresBrokerage: false,
      licenseKind: null,
      note: "New Zealand does not require a business broker licence for going-concern sales. If you hold a licence under the Real Estate Agents Act 2008, you can confirm that so ads can show it.",
      defaultCurrency: "NZD",
      attestationKind: "NZ_REAA_2008",
      attestationRequiredWithLicense: false,
      showsLicenseNumber: false,
      licenseNumberOptional: false,
    };
  }

  const requiresRealEstateLicense = REAL_ESTATE_LICENSE_STATES.has(normalized);
  const special = SPECIAL_REQUIREMENTS[normalized];

  const requiresLicense =
    special?.requiresLicense ?? requiresRealEstateLicense;

  return {
    countryCode: country === "US" ? "US" : "",
    stateCode: normalized,
    requiresLicense,
    requiresBrokerage:
      special?.requiresBrokerage ?? requiresRealEstateLicense,
    licenseKind:
      special?.licenseKind ??
      (requiresRealEstateLicense ? "real_estate_license" : null),
    note: special?.note ?? null,
    defaultCurrency,
    attestationKind: requiresLicense ? "US_ACTIVE_LICENSE" : null,
    attestationRequiredWithLicense: requiresLicense,
    showsLicenseNumber: requiresLicense,
    licenseNumberOptional: false,
  };
}

export function isBrokerAttestationSatisfied(input: {
  requirements: BrokerStateRequirements;
  licenseNumber?: string | null;
  attested: boolean;
}): boolean {
  const license = input.licenseNumber?.trim();
  if (
    input.requirements.attestationKind === "US_ACTIVE_LICENSE" &&
    input.requirements.requiresLicense
  ) {
    return input.attested;
  }
  if (
    input.requirements.attestationKind === "CA_PROVINCIAL_REGISTRATION" &&
    license
  ) {
    return input.attested;
  }
  return true;
}

export function getMissingBrokerPrerequisites(
  snapshot: BrokerPrerequisiteSnapshot,
): BrokerPrerequisiteField[] {
  const missing: BrokerPrerequisiteField[] = [];

  if (!filled(snapshot.imageUrl)) missing.push("photo");
  if (!filled(snapshot.name)) missing.push("name");
  if (!filled(snapshot.phone)) missing.push("phone");
  if (!filled(snapshot.email)) missing.push("email");
  if (!normalizeBrokerCountryCode(snapshot.countryCode)) {
    missing.push("country");
  }
  if (!filled(snapshot.stateCode)) missing.push("state");

  const requirements = getBrokerStateRequirements(
    snapshot.stateCode,
    snapshot.countryCode,
  );
  if (requirements.requiresLicense && !filled(snapshot.licenseNumber)) {
    missing.push("licenseNumber");
  }
  if (requirements.requiresBrokerage && !filled(snapshot.brokerageName)) {
    missing.push("brokerage");
  }

  return missing;
}
